import { Button, Heading, HeadingLevel } from "@ariakit/react";
import { MiddotSeparated } from "@indietabletop/appkit";
import { Fragment } from "react/jsx-runtime";
import { EditMode } from "../../engine/EditMode.ts";
import type { WeaponSpecialRule } from "../../engine/Ruleset.ts";
import type { GitWeapon, VehicleWeapon } from "../../engine/Weapon.ts";
import type { LookupResult } from "../../oss/lookup.ts";
import { caps, compactDamage, hands, list } from "../fmt.ts";
import { Divider } from "../forms/Divider.tsx";
import { ItemNotFound } from "./ItemNotFound.tsx";
import { LoadoutItem } from "./LoadoutItem.tsx";
import { useModelContext } from "./ModelContext.tsx";
import * as css from "./style.css.ts";
import { useGangContext } from "./useGang.ts";
type AnyWeapon = GitWeapon | VehicleWeapon;

function WeaponRow<T extends AnyWeapon>(props: {
  weapon: T;
  onRemoveWeapon?: ((weapon: Pick<T, "index">) => void) | null;
}) {
  const { readOnly, gang } = useGangContext();
  const { weapon, onRemoveWeapon } = props;
  const rulesList = list(weapon.rules);
  const placement = "placement" in weapon ? weapon.placement : null;
  const showCost = gang.mode.itemCostVisibility === "VISIBLE";

  return (
    <div className={css.weaponsSection.weaponRow}>
      <div className={css.weaponsSection.weaponHeader}>
        <div className={css.weaponsSection.weaponName}>
          {weapon.name}
          {weapon.count > 1 ? (
            <span style={{ opacity: "0.5", fontWeight: 400 }}>
              {" "}
              (x{weapon.count})
            </span>
          ) : null}
        </div>

        {weapon.id !== "headbutt" && (
          <MiddotSeparated className={css.weaponsSection.summary}>
            <>
              {showCost && (
                <span className={css.weaponsSection.cost}>
                  {caps(weapon.cost)}
                  {", "}
                </span>
              )}

              {placement ?? hands(weapon.hands)}
            </>

            {rulesList}
          </MiddotSeparated>
        )}
      </div>

      <div>{weapon.range}</div>
      <div>{weapon.dice}</div>
      <div>{compactDamage(weapon.damage)}</div>

      {!readOnly && onRemoveWeapon && (
        <Button
          className={css.weaponsSection.removeButton}
          onClick={() => void onRemoveWeapon(weapon)}
        >
          X
        </Button>
      )}
    </div>
  );
}

export function WeaponsSection<T extends AnyWeapon>(props: {
  weapons: LookupResult<T>[];
  headbutt?: GitWeapon;
  weaponSpecialRules: LookupResult<WeaponSpecialRule>[];
  onRemoveWeapon: (weapon: Pick<T, "index">) => void;
}) {
  const model = useModelContext();
  const canRemoveWeapons = model.can(EditMode.REMOVE_FROM_LIST, "weapons");
  const { weapons, headbutt, weaponSpecialRules, onRemoveWeapon } = props;

  return (
    <div>
      <Heading className={css.common.sectionHeading}>Weapons</Heading>

      <div>
        <div className={css.weaponsSection.headerRow}>
          <div className={css.weaponsSection.label({ align: "start" })}>
            Name
          </div>
          <div className={css.weaponsSection.label({ align: "center" })}>
            Range
          </div>
          <div className={css.weaponsSection.label({ align: "center" })}>
            Dice
          </div>
          <div className={css.weaponsSection.label({ align: "center" })}>
            Damage
          </div>
        </div>

        <Divider />

        {headbutt && (
          <>
            <WeaponRow weapon={headbutt} />
            <Divider />
          </>
        )}

        {weapons.map((lookup, index) => {
          if (lookup.isFailure) {
            return (
              <Fragment key={index}>
                <ItemNotFound
                  result={lookup}
                  item="a weapon"
                  onRemove={() => void onRemoveWeapon({ index })}
                />
                <Divider />
              </Fragment>
            );
          }

          const handleRemove = canRemoveWeapons ? onRemoveWeapon : null;

          return (
            <Fragment key={index}>
              <WeaponRow weapon={lookup.value} onRemoveWeapon={handleRemove} />
              <Divider />
            </Fragment>
          );
        })}
      </div>

      {weaponSpecialRules.length > 0 && (
        <HeadingLevel>
          <div className={css.weaponsSection.specialRules}>
            <div className={css.common.itemsList}>
              {weaponSpecialRules.flatMap((lookupResult, index) => {
                if (lookupResult.isFailure) {
                  return (
                    <ItemNotFound
                      key={index}
                      result={lookupResult}
                      item="a special rule"
                      onRemove={null} // Can't remove a special rule
                    />
                  );
                }

                return (
                  <LoadoutItem
                    key={index}
                    item={lookupResult.value}
                    onRemove={null}
                  />
                );
              })}
            </div>
          </div>
        </HeadingLevel>
      )}
    </div>
  );
}
